import React from 'react';

import {
  getColumnSearchProps,
  getColumnSearchPropsWithPlaceholderText,
} from './colum-search-props';
import { fieldExplanationMap, nodesInfoSorter } from './helpers';
import { LinkWithNodes, Node } from './types';

export const EDGES = (
  searchFilter: any,
  setSearchFilter: any,
  searchInputRef: any
) => [
  {
    title: 'Source',
    key: 'source',
    dataIndex: ['source', 'symbol'],
    sorter: (a: LinkWithNodes, b: LinkWithNodes) =>
      a.source.symbol.localeCompare(b.source.symbol),
    ...getColumnSearchProps(
      'source',
      searchFilter,
      setSearchFilter,
      searchInputRef,
      'symbol'
    ),
  },
  {
    title: 'Target',
    key: 'target',
    dataIndex: ['target', 'symbol'],
    sorter: (a: LinkWithNodes, b: LinkWithNodes) =>
      a.target.symbol.localeCompare(b.target.symbol),
    ...getColumnSearchProps(
      'target',
      searchFilter,
      setSearchFilter,
      searchInputRef,
      'symbol'
    ),
  },
  {
    title: 'Correlation',
    key: 'correlation',
    dataIndex: 'correlation',
    render: (corr: number) => <span>{corr?.toFixed(3)}</span>,
  },
  {
    title: 'Pval',
    key: 'pvalue',
    dataIndex: 'pvalue',
    render: (pval: number) => <span>{pval?.toExponential(3)}</span>,
  },
  {
    title: 'Padj',
    key: 'padj',
    dataIndex: 'padj',
    render: (padj: number) => <span>{padj?.toExponential(3)}</span>,
  },
];

export const getInfoContent = (
  type: string,
  infoType: string,
  urlPayload: any,
  textPayload: any
) => {
  let content: string | JSX.Element = textPayload;
  switch (type) {
    case 'Species':
      if (infoType === 'symbol') {
        content = (
          <a
            href={
              'https://www.microbiomeatlas.org/species.php?species_msp=' +
              urlPayload
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            {textPayload}
          </a>
        );
      }
      break;
  }
  return content;
};

export const getLinkComponent = (node: any) => {
  return (
    <a
      href={
        'https://www.microbiomeatlas.org/species.php?species_msp=' +
        node?.indexClone
      }
      target="_blank"
      rel="noopener noreferrer"
    >
      {node?.symbol}
    </a>
  );
};

export const analyteColumnCreator = (
  analyteType: string,
  searchFilter: any,
  setSearchFilter: any,
  searchInputRef: any
) => {
  return [
    {
      title: fieldExplanationMap[analyteType].symbol,
      key: 'symbol',
      dataIndex: 'symbol',
      sorter: (a: Node, b: Node) => a.symbol?.localeCompare(b.symbol),
      ...getColumnSearchProps(
        'symbol',
        searchFilter,
        setSearchFilter,
        searchInputRef
      ),
      render: (text: any, record: any) =>
        getInfoContent(analyteType, 'symbol', record['node_id'], text),
    },
    ...(fieldExplanationMap[analyteType].info1
      ? [
          {
            title: fieldExplanationMap[analyteType].info1,
            key: 'info1',
            dataIndex: 'info1',
            sorter: (a: Node, b: Node) => a.info1?.localeCompare(b.info1),
            render: (text: any) =>
              getInfoContent(analyteType, 'info1', text, text),
          },
        ]
      : []),
    ...(fieldExplanationMap[analyteType].info2
      ? [
          {
            title: fieldExplanationMap[analyteType].info2,
            key: 'info2',
            dataIndex: 'info2',
            sorter: (a: Node, b: Node) => a.info2?.localeCompare(b.info2),
            render: (text: any) =>
              getInfoContent(analyteType, 'info2', text, text),
          },
        ]
      : []),
    ...(fieldExplanationMap[analyteType].info3
      ? [
          {
            title: fieldExplanationMap[analyteType].info3,
            key: 'info3',
            dataIndex: 'info3',
            sorter: (a: Node, b: Node) => a.info3?.localeCompare(b.info3),
            render: (text: any) =>
              getInfoContent(analyteType, 'info3', text, text),
          },
        ]
      : []),
    {
      title: fieldExplanationMap[analyteType].location,
      key: 'location',
      dataIndex: 'location',
      sorter: (a: Node, b: Node) => a.location?.localeCompare(b.location),
    },
    {
      title: fieldExplanationMap[analyteType].cluster,
      key: 'cluster',
      dataIndex: 'cluster',
    },
  ];
};

const defaultSorter = (a: any, b: any, key: any) => {
  let result = a[key]?.localeCompare(b[key]);

  if (!a[key] || !b[key]) {
    result = a[key] ? 1 : b[key] ? -1 : 0;
  }

  if (!isNaN(a[key]) && !isNaN(b[key])) {
    result = parseInt(a[key]) - parseInt(b[key]);
  }

  return result;
};

const getSorter = (options: any, key: any) => {
  return options[key]?.disableSorter
    ? {}
    : {
        sorter: (a: any, b: any) => defaultSorter(a, b, key),
      };
};

const defaultRender = (text: any) => (
  <div className="custom-scrollbar float-left max-w-xs text-sm whitespace-nowrap overflow-x-auto">
    {text}
  </div>
);

const colorRenderer = (color: string) => (
  <div className="flex items-center justify-center">
    <div
      className="w-4 h-4 rounded-full"
      style={{
        backgroundColor: color,
      }}
    ></div>
  </div>
);

const getRender = (options: any, key: any) => {
  return options[key]?.render
    ? { render: options[key].render }
    : {
        render: defaultRender,
      };
};

export const getNodeTableColumns = (
  nodesInfo: any,
  searchFilter: any,
  setSearchFilter: any,
  searchInputRef: any
) => {
  const options: any = {
    color: {
      render: colorRenderer,
      disableSearch: true,
    },
    coloredField: { disableSearch: true, disableSorter: true },
  };

  const columnNames: any = {
    symbol: 'Name',
    color: 'Color',
    coloredField: 'Colored Field',
    location: 'Location',
    indexClone: 'Index',
    Degree: 'Degree',
  };

  Object.keys(nodesInfo).forEach((nodeInfo) => {
    columnNames[nodeInfo] = nodeInfo;
  });

  const columns = Object.keys(columnNames)
    .sort(nodesInfoSorter)
    .map((key: any) => ({
      title: columnNames[key],
      dataIndex: key,
      key,
      ...getSorter(options, key),
      ...getColumnSearchPropsWithPlaceholderText(
        options[key]?.disableSearch,
        key,
        columnNames[key],
        searchFilter,
        setSearchFilter,
        searchInputRef
      ),
      ...getRender(options, key),
    }));

  return columns;
};
